import axios from "axios";
import { WHITELIST_401 } from "../consts";
import { config } from "../consts/config";
import qs from "qs";

const instance = axios.create({
  baseURL: config.url.BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const getData = async (
  url,
  params = null,
  noToken = false,
  useParamsSerializer,
) => {
  const accessToken = JSON.parse(window.localStorage.getItem("accessToken"));
  let headers;
  if (accessToken === null || noToken) {
    headers = null;
  } else {
    headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }
  if (useParamsSerializer) {
    return await instance.get(url, {
      headers: headers,
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
  }
  return await instance.get(url, {
    headers: headers,
    params: params,
  });
};

const postData = async (url, body = {}, noToken = false) => {
  const accessToken = JSON.parse(window.localStorage.getItem("accessToken"));
  let headers;
  if (accessToken === null || noToken) {
    headers = null;
  } else {
    headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return await instance.post(url, JSON.stringify(body), {
    headers: headers,
  });
};

const putData = async (url, body = {}) => {
  const accessToken = JSON.parse(window.localStorage.getItem("accessToken"));
  return await instance.put(url, JSON.stringify(body), {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const deleteData = async (url) => {
  const accessToken = JSON.parse(window.localStorage.getItem("accessToken"));
  try {
    // console.log({data})
    return await instance.delete(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch (err) {
    if (err.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log(err.response.data);
      console.log(err.response.status);
      console.log(err.response.headers);
    } else if (err.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log(err.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log("Error", err.message);
    }
    return err;
  }
};

// User APIs
export const loginUser = async (obj) => {
  return await postData("/login", obj);
};

export const logoutUser = async () => {
  const refreshToken = JSON.parse(window.localStorage.getItem("refreshToken"));
  return await postData("/logout", { refresh_token: refreshToken });
};

export const forgotPassword = async (body) => {
  return await postData("/forgot-password", body);
};

export const resetPassword = async (token, body) => {
  return await postData(`/reset-password/${token}`, body);
};

const refresh = async () => {
  const refreshToken = JSON.parse(window.localStorage.getItem("refreshToken"));
  return await postData("/refresh", { refresh_token: refreshToken }, true);
};

export const getOrg = async (orgId) => {
  return await getData(`/organization/${orgId}`);
};

export const getProfile = async () => {
  return await getData("/user/profile");
};

export const editProfile = async (body) => {
  return await putData("/user/profile", body);
};

export const changePassword = async (body) => {
  return await postData("/user/change-password", body);
};

// ORG TABLE APIs
export const getOrgs = async () => {
  return await getData("/organization");
};

export const getOrgData = async (orgId) => {
  return await getData(`/organization/${orgId}`);
};

export const putOrg = async (orgId, body) => {
  return await putData(`/organization/${orgId}`, body);
};

export const postOrg = async (body) => {
  return await postData("/organization", body);
};

export const putOrgStatus = async (orgId, body) => {
  return await putData(`/organization/${orgId}/status`, body);
};

export const deleteOrg = async (orgId, body) => {
  return await deleteData(`/organization/${orgId}`, body);
};

export const uploadLogo = async (orgId, body) => {
  return await putData(`organization/${orgId}/upload-logo`, body);
};

export const getLogoUploadUrl = async (orgId, extension, type) => {
  let params = { file_extension: extension, content_type: type };
  return await getData(`organization/${orgId}/get-presigned-url`, params);
};

// SITE TABLE APIs
export const getSites = async (orgId) => {
  return await getData(`/site`, { organization: orgId });
};

export const deleteSite = async (siteId, body) => {
  return await deleteData(`/site/${siteId}`, body);
};

export const putSite = async (siteId, body) => {
  return await putData(`/site/${siteId}`, body);
};

export const updateSiteMetadata = async (siteId, body) => {
  return await putData(`/site/${siteId}/metadata`, body);
};

export const getSite = async (siteId) => {
  return await getData(`/site/${siteId}`);
};

export const putSiteStatus = async (siteId, body) => {
  return await putData(`/site/${siteId}/status`, body);
};

export const postSite = async (body) => {
  return await postData("/site", body);
};

export const getCountries = async () => {
  try {
    return await instance.get(
      "https://countriesnow.space/api/v0.1/countries/flag/unicode",
    );
  } catch (err) {
    console.log({ err });
    return err;
  }
};

export const getCities = async (body) => {
  try {
    return await instance.post(
      "https://countriesnow.space/api/v0.1/countries/cities",
      JSON.stringify(body),
    );
  } catch (err) {
    console.log({ err });
    return err;
  }
};

// PLOT TABLE APIs
export const getPlots = async (org, site, crop, harvestDate = null) => {
  let params = { organization: org, site_id: site };
  if (crop) {
    params.crop_id = crop;
  }
  if (harvestDate) {
    params.harvest_date = harvestDate;
  }
  return await getData("/plot", params);
};

export const getPlot = async (plotId) => {
  let params = { plot_id: plotId };
  return await getData(`/plot/${plotId}`, params);
};

export const createPlot = async (data) => {
  return await postData("/plot", data);
};

export const updatePlot = async (plotId, data) => {
  return await putData(`/plot/${plotId}`, data);
};

export const deletePlot = async (plotId) => {
  return await deleteData(`/plot/${plotId}`);
};

export const getHarvestedPlots = async (
  org,
  site,
  crop,
  greenhouseIds = null,
  plotIds = null,
  harvestDate = null,
) => {
  let body = { organization: org, site_id: site, crop_id: crop };
  if (greenhouseIds) {
    body.greenhouse_ids = greenhouseIds;
  }
  if (plotIds) {
    body.plot_ids = plotIds;
  }
  if (harvestDate) {
    body.harvest_date = harvestDate;
  }
  return await postData("/plot/harvested-date", body);
};

export const getHarvestEntryConfig = async (siteId, cropId, orgId = null) => {
  const params = { site: siteId, crop: cropId };
  if (orgId != null) params.organization = orgId;
  return await getData("/plot/harvest-entry-config", params);
};

export const updateHarvestedPlots = async (
  org,
  siteId,
  cropId,
  greenhouseId,
  plotId,
  harvestedDate,
  totalYield = null,
  totalWeight = null,
  greenhouseName = null,
  plotName = null,
  weightPerTub = undefined,
  weightPerTubSource = undefined,
  ploughedUnderReasons = undefined,
  ploughedUnderNote = undefined,
) => {
  const data = {
    organization: org,
    site: siteId,
    crop: cropId,
    greenhouse: greenhouseId,
    plot: plotId,
    harvested_date: harvestedDate,
    total_yield: totalYield,
    total_weight: totalWeight,
    greenhouse_name: greenhouseName,
    plot_name: plotName,
  };
  if (weightPerTub !== undefined) data.weight_per_tub = weightPerTub;
  if (weightPerTubSource !== undefined) data.weight_per_tub_source = weightPerTubSource;
  if (ploughedUnderReasons !== undefined) data.ploughed_under_reasons = ploughedUnderReasons;
  if (ploughedUnderNote !== undefined) data.ploughed_under_note = ploughedUnderNote;
  return await putData("/plot/harvested-date", data);
};

export const updateUnHarvestedPlots = async (
  org,
  siteId,
  cropId,
  greenhouseId,
  plotId,
  greenhouseName = null,
  plotName = null,
) => {
  let data = {
    organization: org,
    site: siteId,
    crop: cropId,
    greenhouse: greenhouseId,
    plot: plotId,
    greenhouse_name: greenhouseName,
    plot_name: plotName,
  };
  return await putData("/plot/unharvest-date", data);
};

export const getPlotDataVisibility = async (
  org,
  site,
  crop,
  greenhouseIds = null,
  harvestDates = null,
) => {
  let payload = { organization: org, site_id: site, crop_id: crop };
  if (greenhouseIds && Array.isArray(greenhouseIds) && greenhouseIds.length) {
    payload.greenhouse_ids = greenhouseIds;
  }
  if (harvestDates && Array.isArray(harvestDates) && harvestDates.length) {
    payload.harvest_dates = harvestDates;
  }
  return await postData("/plot/plotdata/visibility", payload);
};

export const updatePlotDataVisibility = async (data) => {
  return await putData("/plot/plotdata/visibility", data);
};

export const getForecastedPlots = async (
  org,
  site,
  crop,
  greenhouseIds = null,
  plotIds = null,
  dateOfForecasts = null,
) => {
  let params = { organization: org, site_id: site, crop_id: crop };
  if (greenhouseIds) {
    params.greenhouse_ids = greenhouseIds;
  }
  if (plotIds) {
    params.plot_ids = plotIds;
  }
  if (dateOfForecasts) {
    params.date_of_forecasts = dateOfForecasts;
  }
  return await getData("/plot/forecast-data", params, false, true);
};

export const updateForecastedPlots = async (
  org,
  siteId,
  cropId,
  dateOfForecast,
  forecastData,
) => {
  let data = {
    organization: org,
    site_id: siteId,
    crop_id: cropId,
    date_of_forecast: dateOfForecast,
    data: forecastData,
  };
  return await putData("/plot/forecast-data", data);
};

// SUBPLOT TABLE APIs
export const getSubPlots = async (
  org,
  site,
  crop,
  plotIds = null,
  harvestDates = null,
  greenhouseId = null,
  basicData = true,
) => {
  let params = { organization: org, site_id: site, basic_data: basicData };
  if (crop) {
    params.crop_id = crop;
  }
  if (harvestDates) {
    params.harvest_dates = harvestDates;
  }
  if (greenhouseId) {
    params.greenhouse_id = greenhouseId;
  }
  if (plotIds) {
    params.plot_ids = plotIds;
  }
  return await getData("/plot/subplot", params, false, true);
};

export const createSubPlot = async (subplotData) => {
  return await postData("/plot/subplot", subplotData);
};

export const updateSubPlot = async (subplotId, subplotData) => {
  return await putData(`/plot/subplot/${subplotId}`, subplotData);
};

export const deleteSubPlot = async (subplotId) => {
  return await deleteData(`/plot/subplot/${subplotId}`);
};

// Variety Table APIs
export const getVarieties = async (org, site, crop, harvestDate = null) => {
  let params = { organization: org, site_id: site };
  if (crop) {
    params.crop_id = crop;
  }
  if (harvestDate) {
    params.harvest_date = harvestDate;
  }
  return await getData("/variety", params);
};

export const createVariety = async (data) => {
  return await postData("/variety", data);
};

export const updateVariety = async (varietyId, data) => {
  return await putData(`/variety/${varietyId}`, data);
};

export const deleteVariety = async (varietyId) => {
  return await deleteData(`/variety/${varietyId}`);
};

export const fetchVarietyData = async (
  org,
  site,
  crop,
  greenhouseIds,
  varietyIds,
  harvestDates,
) => {
  let params = { organization: org, site_id: site };
  if (crop) {
    params.crop_id = crop;
  }
  if (greenhouseIds) {
    params.greenhouse_ids = greenhouseIds;
  }
  if (varietyIds) {
    params.variety_ids = varietyIds;
  }
  if (harvestDates) {
    params.dates = harvestDates;
  }
  return await getData(`/variety/variety-data`, params, false, true);
};

export const UpdateVarietyHarvestedData = async (org, site, crop, data) => {
  let body = { organization: org, site_id: site, crop_id: crop, data: data };
  return await putData(`/variety/variety-data/harvested-data`, body);
};

export const copyPreviousVarietyHarvestedData = async (
  org,
  site,
  crop,
  greenhouseIds,
  varietyIds,
  dates,
  copyDateBefore = 7,
) => {
  let body = {
    organization: org,
    site_id: site,
    crop_id: crop,
    greenhouse_ids: greenhouseIds,
    variety_ids: varietyIds,
    dates,
    day: copyDateBefore,
  };
  return await putData(`/variety/copy-data`, body);
};

// USER TABLE APIs
export const getUsers = async (org, site) => {
  let params = { organization: org };
  if (site) {
    params.site_id = site;
  }
  return await getData("/user", params);
};

export const addUser = async (body) => {
  return await postData("/user/register", body);
};

export const putUser = async (userId, body) => {
  return await putData(`/user/${userId}`, body);
};

export const deleteUser = async (userId, body) => {
  return await deleteData(`/user/${userId}`, body);
};

export const putUserStatus = async (userId, body) => {
  return await putData(`/user/${userId}/status`, body);
};

export const resendInvite = async (userId) => {
  return await postData(`/resend-invite/${userId}`, {});
};

// CROP TABLE APIs
export const getCrops = async (site = null) => {
  return await getData("/crop", { site_id: site });
};

export const deleteCrop = async (cropId, body) => {
  return await deleteData(`/crop/${cropId}`, body);
};

export const putCrop = async (cropId, body) => {
  return await putData(`/crop/${cropId}`, body);
};

export const putCropStatus = async (cropId, body) => {
  return await putData(`/crop/${cropId}/status`, body);
};

export const postCrop = async (body) => {
  return await postData("/crop", body);
};

//GREENHOUSE TABLE APIs
export const putGreenhouse = async (greenhouseId, body) => {
  return await putData(`/greenhouse/${greenhouseId}`, body);
};

export const postGreenhouse = async (body) => {
  return await postData(`/greenhouse`, body);
};
export const deleteGreenhouse = async (greenhouseId) => {
  return await deleteData(`/greenhouse/${greenhouseId}`);
};

// DASHBOARD APIs
export const getGraphData = async (
  orgId,
  siteId,
  cropId,
  plotId = null,
  harvestDate = null,
  greenhouseId = null,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    plot_id: plotId,
    greenhouse_id: greenhouseId,
    harvest_date: harvestDate,
  };

  if (Array.isArray(plotId)) {
    params.plot_id = JSON.stringify(plotId);
  }
  if (Array.isArray(harvestDate)) {
    params.harvest_date = JSON.stringify(harvestDate);
  }

  return await getData("/plot/harvest-date/plots", params);
};

export const getHarvestDates = async (orgId, siteId, cropId) => {
  let params = { organization_id: orgId, site_id: siteId, crop_id: cropId };
  return await getData("/harvest", params);
};

export const getSubplotDataSize = async (
  orgId,
  siteId,
  cropId,
  plotId,
  date,
) => {
  let params = { organization: orgId, site_id: siteId, crop_id: cropId };
  return await getData(
    `plot/${plotId}/harvest-date/${date}/subplot/size`,
    params,
  );
};

export const getSubplotData = async (
  orgId,
  siteId,
  cropId,
  plotId,
  date,
  page,
  page_size,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    page: page,
    page_size: page_size,
  };
  return await getData(`plot/${plotId}/harvest-date/${date}/subplot`, params);
};

export const getSubplotGraphData = async (orgId, siteId, cropId, plotId) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    plot_id: plotId,
  };
  return await getData("plot/harvest-date/subplot", params);
};

export const getDatesForSubplotData = async (
  orgId,
  siteId,
  cropId,
  greenhouseIds,
  plotIds,
  subplotIds,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    greenhouse_ids: greenhouseIds,
  };
  if (plotIds) {
    params.plot_ids = plotIds;
  }
  if (subplotIds) {
    params.subplot_ids = subplotIds;
  }
  return await getData("plot/subplot-dates", params, false, true);
};

export const getSubplotDataDetails = async (
  orgId,
  siteId,
  cropId,
  greenhouseIds,
  plotIds,
  subplotIds,
  date,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    greenhouse_ids: greenhouseIds,
    date: date,
  };
  if (plotIds) {
    params.plot_ids = plotIds;
  }
  if (subplotIds) {
    params.subplot_ids = subplotIds;
  }
  return await getData("plot/subplot-data-detail", params, false, true);
};

// RIPENESS CRITERIA APIs
export const getRipenessData = async (plotId, cropId, date) => {
  let params = { plot_id: plotId, crop_id: cropId, harvest_date: date };
  return await getData(`user/ripeness-criteria`, params);
};

export const putRipenessData = async (body) => {
  return await putData("user/ripeness-criteria", body);
};

//FLIGHT LOGS APIs
export const getFlightLogs = async (siteId, startDate, endDate) => {
  let params = {
    site_id: siteId,
    start_timestamp: startDate,
    end_timestamp: endDate,
  };
  return await getData("/data-sync/flight-logs", params);
};

// Fetch new access_token and retry request if 401 - token is expired
instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async (error) => {
    const refreshToken = JSON.parse(
      window.localStorage.getItem("refreshToken"),
    );
    if (401 === error.response.status) {
      if (!refreshToken || WHITELIST_401.includes(error.config.url)) {
        window.localStorage.setItem("accessToken", null);
        window.localStorage.setItem("refreshToken", null);
        if (error.config.url === "/refresh" || error.config.url === "/logout") {
          window.location.replace(window.location.origin);
          window.localStorage.clear();
        }
        return Promise.reject(error);
      } else {
        const res401 = await refresh();
        const accessToken = res401?.data?.data?.access_token;
        window.localStorage.setItem("accessToken", JSON.stringify(accessToken));
        error.config.headers["Authorization"] = "Bearer " + accessToken;
        return instance.request(error.config);
      }
    } else {
      return Promise.reject(error);
    }
  },
);

// Grower APIs
export const getGreenhouses = async (org, site, crop = null) => {
  const params = {
    organization: org,
    site_id: site,
  };
  if (crop) {
    params["crop_id"] = crop;
  }
  return await getData("/greenhouse", params);
};

// Grower APIs
export const getGreenhousePlots = async (org, site, crop, greenhouse) => {
  const params = {
    organization: org,
    site_id: site,
    crop_id: crop,
  };
  return await getData(`/greenhouse/${greenhouse}/plots`, params);
};

export const getGreenhousePlotData = async (
  org,
  site,
  crop,
  greenhouseIds,
  harvestDates,
) => {
  const body = {
    organization: org,
    site_id: site,
    crop_id: crop,
  };
  if (greenhouseIds) {
    body.greenhouse_ids = greenhouseIds;
  }
  if (harvestDates) {
    body.harvest_dates = harvestDates;
  }
  return await postData(`/greenhouse/plot-data`, body);
};

export const getGreenhouseData = async (
  orgId,
  siteId,
  cropId,
  greenhouseId,
  harvest_date,
  ripe,
  fraction,
  immediate_ripe,
  immediate_fraction,
  floor_limit,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
    greenhouse_id: greenhouseId,
    harvest_date: harvest_date,
    ripe: ripe,
    fraction: fraction,
    immediate_ripe: immediate_ripe,
    immediate_fraction: immediate_fraction,
    floor_limit: floor_limit,
  };

  return await getData("/greenhouse/data", params);
};

export const getForecastDetail = async (siteId) => {
  return await getData(`/site/${siteId}/forecast`);
};

export const getAllPlotData = async (orgId, siteId, cropId) => {
  const params = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
  };

  return await getData("/plot/harvest-date/all-plots", params);
};

export const getExcelData = async (
  orgId,
  siteId,
  cropId,
  greenhouseName = null,
  greenhouseNames = null,
) => {
  const body = {
    organization: orgId,
    site_id: siteId,
    crop_id: cropId,
  };
  if (greenhouseName) {
    body.greenhouse_name = greenhouseName;
  }
  if (greenhouseNames) {
    body.greenhouse_names = greenhouseNames;
  }
  return await postData("/greenhouse/plantingDate", body);
};

export const getFilePreSignedUrl = async (
  orgId,
  siteId,
  cropName,
  path,
  cloud = null,
  useCdn = false,
  checkFileExist = false,
) => {
  let params = {
    organization: orgId,
    site_id: siteId,
    crop_name: cropName,
    path: path,
    cloud: cloud,
    use_cdn: useCdn,
    check_file_exist: checkFileExist,
  };
  return await getData("/data-sync/get-file-url", params);
};

export const getPipelineLogs = async (
  siteId,
  cropId,
  startTime = null,
  endTime = null,
  status = null,
) => {
  let params = {
    site_id: siteId,
    crop_id: cropId,
    status: status,
    start_timestamp: startTime,
    end_timestamp: endTime,
  };
  return await getData("/data-sync/pipeline-logs", params);
};

export const getAllowedRoles = async (orgId) => {
  let params = {
    organization: orgId,
  };
  return await getData("/user/roles", params);
};

export const getPermissions = async () => {
  return await getData("/user/permissions", {});
};

export const addPermission = async (data) => {
  return await postData("/user/permissions", data);
};

export const updatePermission = async (permissionId, data) => {
  return await putData("/user/permissions/" + permissionId, data);
};
export const deletePermission = async (permissionId) => {
  return await deleteData("/user/permissions/" + permissionId);
};

export const getRoles = async () => {
  return await getData("/user/roles", {});
};

export const addRole = async (data) => {
  return await postData("/user/roles", data);
};

export const updateRole = async (roleId, data) => {
  return await putData("/user/roles/" + roleId, data);
};

export const deleteRole = async (roleId) => {
  return await deleteData("/user/roles/" + roleId);
};

export const getBroccoliOptimiserForecastHarvestPlan = async (organization, site, constraints) => {
  const response = (await postData("/optimizer/broccoli/harvest-plan/", { organization, site, constraints }));
  return response.data;
}

export const recomputeHarvestPlan = async (organization, site ) => {
  return await postData("/optimizer/broccoli/projection-trigger/", { organization, site });
}